import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  ComputedDataKey,
  createRouteRebuildAppliedAt,
  editFormDataAF,
  EditFormMode,
  errors,
  IApiError,
  IStationRouteComputedData,
  IStationRouteDto,
  IStationRouteStationInfo,
  IUpdateStationRouteDistancesCriteria,
  Log,
  logBoolean,
  mergeRoutingWaypointsIntoRecountedStationsInfo,
  normalizeErrors,
  normalizeStationRouteComputedData,
  RecountStationRouteDistancesCriteriaDto,
  RecountStationsRouteResultDto,
  SimpleResult,
  StationRouteApi,
  useTimezone,
  withStationRouteRoutingEngineMetadata,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import { useStationRouteStationsUpdateInfo } from "./use-station-route-update-info";

export const useStationRouteDtoActions = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { mode } = useCrudEditContext<IStationRouteDto>();
  const [stationRouteDtoGlobal, setStationRouteDtoGlobal] = useRecoilState<
    Partial<IStationRouteDto> | undefined
  >(editFormDataAF(["stationRoute", mode || EditFormMode.CrudRoute]));

  const { setStationRouteHasStationChangeComputed } =
    useStationRouteStationsUpdateInfo();

  const mergeRouteDataToComputedData = (
    recounted: RecountStationsRouteResultDto,
    preserveStationOrder = false,
    sourceStations?: IStationRouteStationInfo[],
  ): void => {
    if (recounted.existingRouteId) {
      Log.debug("Using route from cache with id" + recounted.existingRouteId);
    }

    setStationRouteDtoGlobal(dto => {
      const currentComputedData: IStationRouteComputedData = {
        ...(dto?.computedData || {}),
      };
      const { stationsInfo, result } = recounted;
      // Recount/cache can omit newly added map waypoints — merge them back from the request.
      currentComputedData.stationInfos =
        mergeRoutingWaypointsIntoRecountedStationsInfo({
          recounted: stationsInfo ?? [],
          source: sourceStations?.length
            ? sourceStations
            : (dto?.computedData?.stationInfos ?? []),
        });

      const computedDataRecounted = normalizeStationRouteComputedData(
        currentComputedData,
        !preserveStationOrder,
        timezone,
      );
      const routingEngine = recounted.directionsService ?? recounted.provider;
      const recountedStationRouteDto = {
        ...dto,
        result,
        [ComputedDataKey]: computedDataRecounted,
        ...(routingEngine
          ? {
              metadata: withStationRouteRoutingEngineMetadata({
                metadata: dto?.metadata,
                engine: routingEngine,
                routeRebuildAt: createRouteRebuildAppliedAt(),
              }),
            }
          : {}),
      };
      Log.debug(
        "Station route route rebuilt successfully",
        recountedStationRouteDto,
      );
      return recountedStationRouteDto;
    });
  };

  const updateStationRouteComputedDataRecounted = useEvent(
    async ({
      computedData,
      optimize = false,
      direction,
      skipApiCheck = false,
    }: IUpdateStationRouteDistancesCriteria): Promise<SimpleResult<void>> => {
      if (skipApiCheck) {
        Log.debug(
          "StationRoute locations updated without api check",
          computedData,
        );
        setStationRouteDtoGlobal(stationRouteDto => ({
          ...stationRouteDto,
          computedData,
        }));
        return new SimpleResult({ success: true });
      }

      Log.debug(
        `StationRoute computed data updating WITH api check (with optimize?- ${logBoolean(optimize)}) `,
        computedData,
      );

      try {
        const criteriaRecount = new RecountStationRouteDistancesCriteriaDto({
          stations: computedData?.stationInfos,
          direction,
          optimize,
        });
        const recounted =
          await StationRouteApi.recountStationInfo(criteriaRecount);

        if (!recounted?.success || !recounted?.data?.stationsInfo) {
          return new SimpleResult({
            success: false,
            error:
              recounted?.error ||
              errors.common.stationsNotRecountedSuccessfully,
          });
        }

        mergeRouteDataToComputedData(
          recounted.data,
          !optimize,
          computedData?.stationInfos,
        );
        return new SimpleResult({ success: true });
      } catch (err) {
        return new SimpleResult({
          success: false,
          error: normalizeErrors(err as IApiError, t),
        });
      }
    },
  );

  const updateEditingStationRouteComputedData = useEvent(
    async ({
      computedData,
      optimize,
      direction,
      skipApiCheck,
    }: IUpdateStationRouteDistancesCriteria): Promise<SimpleResult<void>> => {
      const computedDataRecounted = normalizeStationRouteComputedData(
        computedData,
        false,
        timezone,
      );

      setStationRouteHasStationChangeComputed(
        { ...stationRouteDtoGlobal?.computedData },
        computedData,
      );

      return updateStationRouteComputedDataRecounted({
        computedData: computedDataRecounted,
        optimize,
        direction,
        skipApiCheck,
      });
    },
  );

  const stationRouteRebuildRouteHandler = useEvent(
    async (optimize = true): Promise<SimpleResult<void>> => {
      const stations = stationRouteDtoGlobal?.computedData?.stationInfos || [];

      if (!stations?.length) {
        return new SimpleResult({
          success: false,
          error: "No stations to rebuild",
        });
      }

      const criteriaRecount = new RecountStationRouteDistancesCriteriaDto({
        stations: stations,
        optimize: optimize,
      });

      try {
        const recountedResult =
          await StationRouteApi.recountStationInfo(criteriaRecount);

        if (!recountedResult?.success || !recountedResult.data?.stationsInfo) {
          return new SimpleResult({
            success: false,
            error: t("errors.stationRouteNotRecountedSuccessfully"),
          });
        }

        if (!stationRouteDtoGlobal) {
          return new SimpleResult({
            success: false,
            error: t("errors.invalidStationRouteDistancesInfo"),
          });
        }

        mergeRouteDataToComputedData(recountedResult.data, !optimize, stations);

        return new SimpleResult({ success: true });
      } catch (error) {
        return new SimpleResult({
          error: normalizeErrors(error as IApiError, t),
        });
      }
    },
  );

  return {
    stationRouteRebuildRouteHandler,
    updateEditingStationRouteComputedData,
  };
};
